<template>
    <div>
        <h2 class="title is-3">
            Library groups configuration
        </h2>
        <div class="columns is-desktop has-margin-top2">
            <div class="column is-2">
                <div class="">
                    <spinner
                        v-if="loading"
                        :loading="loading" />
                    <div v-else>
                        <b-field>
                            <b-button
                                label="Clear Selected and Reload"
                                type="is-info"
                                icon-left="refresh"
                                :disabled="!selected"
                                @click="clearSelected" />
                        </b-field>
                        <b-table
                            v-model:selected="selected"
                            :data="tags"
                            default-sort="name"
                            :striped="true"
                            :narrowed="true"
                            :loading="loading"
                            :hoverable="true">
                            <b-table-column
                                v-slot="props"
                                field="name"
                                label="Name"
                                sortable>
                                <a @click="selectTag(props.row)">
                                    {{ props.row.name }}
                                </a>
                            </b-table-column>
                            <template #empty>
                                <section class="section">
                                    <div class="content has-text-grey has-text-centered">
                                        <p>
                                            <b-icon
                                                icon="emoticon-sad"
                                                size="is-large" />
                                        </p>
                                        <p>Nothing here.</p>
                                    </div>
                                </section>
                            </template>
                        </b-table>
                    </div>
                </div>
            </div>
            <spinner
                :loading="loadingGroupInfo"
                :fullscreen="false"
                class="column is-5">
                <div
                    v-if="selected">
                    <div class="block">
                        <div class="columns">
                            <div class="column">
                                <h3 class="title is-4">
                                    Group info
                                </h3>
                            </div>
                            <div class="column is-clearfix">
                                <div class="field is-grouped is-pulled-right">
                                    <p class="control">
                                        <button
                                            v-require-can-edit-tag="selected.name"
                                            class="button is-primary"
                                            @click="save">
                                            <b-icon
                                                icon="floppy"
                                                size="is-small" />
                                            <span>Save</span>
                                        </button>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="block">
                        <b-field
                            label="Name"
                            horizontal>
                            <b-input v-model="selected.name" />
                        </b-field>
                        <b-field
                            label="Description"
                            horizontal>
                            <b-input
                                v-model="selected.description"
                                type="textarea" />
                        </b-field>
                        <b-field
                            horizontal>
                            <tag-attachment
                                v-if="selected"
                                :attachment="attachment"
                                :tag-name="selected.name"
                                @updateAttachment="getAttachment" />
                        </b-field>
                    </div>
                </div>
            </spinner>
            <div
                v-if="selected"
                class="column is-5">
                <item-metadata
                    type="Tag"
                    title="Elements"
                    subtitle="The following informational elements will be included on libraries added to group."
                    :selected-name="selected.name" />
            </div>
        </div>
    </div>
</template>

<script>
    import Spinner from '@/shared/components/Spinner.vue';
    import { requireCanEditTag } from '@/shared/directives/requirePermission';
    import { getAllTagsAsEntities, getAttachmentForTag, updateTag } from '@/shared/helpers/api';
    import { showMixin } from '@/shared/mixins/showMixin.js';
    import TagAttachment from './TagAttachment.vue';
    import ItemMetadata from '@/shared/components/ItemMetadata.vue';

    export default {
        directives: {
            'require-can-edit-tag': requireCanEditTag
        },
        components: {
            ItemMetadata,
            Spinner,
            TagAttachment
        },
        mixins: [
            showMixin
        ],
        data() {
            return {
                loading: false,
                loadingGroupInfo: false,
                tags: [],
                selected: null,
                attachment: null
            };
        },
        async created() {
            await this.fetchTagsFromApi();
        },
        methods: {
            async clearSelected() {
                this.selected = null;
                await this.fetchTagsFromApi();
            },
            selectTag(tag) {
                this.selected = JSON.parse(JSON.stringify(tag));
                this.getAttachment();
            },
            async save() {
                this.loadingGroupInfo = true;
                try {
                    await updateTag(this, this.selected);
                    this.showInfo('Changes saved');
                    await this.fetchTagsFromApi();
                } catch (e) {
                    this.showError('Could not save changes');
                } finally {
                    this.loadingGroupInfo = false;
                }
            },
            async fetchTagsFromApi() {
                this.loading = true;
                this.tags = await getAllTagsAsEntities(this);
                this.loading = false;
            },
            async getAttachment() {
                if (!this.selected) return;
                this.loadingGroupInfo = true;

                // getAttachmentForTag requests return an empty string ""
                // because the underlying getResData does not handle 204s
                // TODO: devOps logged #100077
                const apiResponse = await getAttachmentForTag(this, this.selected.name);
                if (typeof apiResponse === 'object') {
                    this.attachment = apiResponse;
                } else {
                    this.attachment = null;
                }
                this.loadingGroupInfo = false;
            }
        }
    };
</script>
